import React from "react";
import { developersBaseUrl } from "constants";

export const invoiceStartGuideTexts = {
  subtitle:
    "Here are some tips and setup tasks to help you get started with invoices",
  allDone: "You've completed all setup tasks!",
  showPreviousTasks: "Show previous tasks",
  tasksLeft: ({ tasksLeft, minutesLeft }) =>
    `${tasksLeft} tasks left (About ${minutesLeft} minutes)`,
};

/**
 * Content of the steps, keyed by the `step` field coming from the server.
 * A step is rendered only when the server returns it, so adding a key here is
 * safe even if the backend does not expose that step yet.
 */
export const invoiceStartGuideSteps = {
  1: {
    name: "Fill your profile",
    videoUrl: "https://www.youtube.com/embed/zEj9_4BLPbo",
    title: "Fill your profile to start issuing compliant invoices",
    description:
      "Your profile information is used automatically on invoice PDFs and during tax calculations.",
    bullets: [
      {
        label: "Use English for all fields",
        text: "Company name, address, and contact person must be in English (Latin alphabet only).",
      },
      {
        label: "Complete your full address",
        text: "Postcode, country, city, street, and house number will appear on invoices.",
      },
      {
        label: "Add your VAT number",
        text: "Required for tax-compliant invoice generation (except USA/VIR, where a Sales Tax Permit Number is needed).",
      },
      {
        label: "Verify contact details",
        text: "Company email and phone number must be valid and accessible (+XXXXXXXXXXXX format).",
      },
      {
        label: "Save changes",
        text: "Click “Save” after filling all required fields to complete your profile.",
      },
    ],
    note: {
      title: "Please note:",
      items: [
        "Address fields are used directly on invoice PDFs",
        "Company name changes may require document verification",
        "All information must match your official business registration documents",
      ],
    },
    actions: [
      { label: "Go to profile", to: "/profile", variant: "primary" },
    ],
  },

  2: {
    name: "Add your VAT number",
    videoUrl: "https://www.youtube.com/embed/yfyuo0ExqLg",
    title: "Add your VAT number to generate tax-compliant invoices",
    description: (
      <>
        In Lappa Invoice you can either <b>Add a VAT</b> (if you already have
        one) or <b>Order a VAT</b> (if you need Lappa to help you register).
      </>
    ),
    bullets: [
      <>
        <b>Where to find VAT settings:</b> Left menu → <b>Settings</b> →{" "}
        <b>VAT</b> tab
      </>,
      <>
        <b>If you already have a VAT number:</b> click <b>“Add a VAT”</b>
      </>,
      <>
        <b>If you don’t have a VAT number yet:</b> click <b>“Order a VAT”</b>{" "}
        and follow the flow
      </>,
    ],
    note: {
      title: "What you’ll need to fill:",
      items: [
        { label: "Country", text: "where you are VAT-registered" },
        { label: "VAT number", text: "as issued by the tax authority" },
        { label: "Reason for registration", text: "e.g. Distance sales" },
        <>
          <b>Valid from</b> (and <b>Valid till</b> if applicable)
        </>,
      ],
      footer: (
        <>
          Save with <b>“Add registration info”</b> or exit with <b>“Cancel”</b>.
          After saving, the number appears in the VAT registrations list and can
          be marked as <b>Active</b>.
        </>
      ),
    },
    docLink: {
      text: "Need more details?",
      label: "Read the full VAT setup guide",
      href: `${developersBaseUrl}/linvoice/how-to-add-a-vat-number-in-lappa-invoice/`,
    },
    actions: [
      { label: "I already have a VAT", to: "/settings" },
      {
        label: "I need to order a VAT",
        to: "/settings/vat-registration/order",
        variant: "primary",
      },
    ],
  },

  3: {
    name: "Add your PEPPOL number",
    videoUrl: "https://www.youtube.com/embed/zSIkJF8aGTo",
    title: "Add your Peppol ID to send e-invoices via Peppol",
    description: (
      <>
        In L’Invoice you can either <b>Add an existing Peppol ID</b> or
        <b> Register a new one</b> if you don’t have it yet.
      </>
    ),
    bullets: [
      <>
        <b>Where to find Peppol settings:</b> Left menu → <b>Settings</b> →{" "}
        <b>PEPPOL</b>
      </>,
      <>
        <b>If you already have a Peppol ID:</b> click <b>“Add PEPPOL Number”</b>
      </>,
      <>
        <b>If you don’t have one:</b> start the <b>Peppol registration</b> flow
      </>,
    ],
    note: {
      title: "For existing Peppol ID:",
      items: [
        { label: "Country", text: "registration country" },
        <>
          <b>Peppol ID</b> — must include a colon <b>:</b>
        </>,
      ],
      footer: (
        <>
          After submission, the Peppol ID is sent to a manager for review. Only
          IDs marked as <b>Approved</b> (green check ✅) can be used.
        </>
      ),
    },
    docLink: {
      text: "Need full instructions?",
      label: "Read the complete Peppol setup guide",
      href: `${developersBaseUrl}/linvoice/guide-for-adding-a-peppol-id/`,
    },
    actions: [
      { label: "I already have a Peppol ID", to: "/settings" },
      {
        label: "Register a new Peppol ID",
        to: "/settings",
        variant: "primary",
      },
    ],
  },

  4: {
    name: "Add bank information",
    videoUrl: "https://www.youtube.com/embed/3LfWV8TA3dw",
    title: "Add bank details for PEPPOL invoices",
    description:
      "Bank details are used in PEPPOL documents and appear in the invoice payment section.",
    bullets: [
      <>
        <b>Where to find bank settings:</b> Left menu → <b>Settings</b> →{" "}
        <b>Bank details</b>
      </>,
      <>
        Click <b>“Add bank account”</b> to add a new account
      </>,
      <>
        Select the <b>currency</b> and choose <b>Local</b> or <b>Global</b> type
      </>,
    ],
    note: {
      title: "What you’ll need to fill:",
      items: [
        <b>Bank name</b>,
        <b>Account holder</b>,
        {
          label: "Account details",
          text: "IBAN / Account number (depends on currency and type)",
        },
        {
          label: "BIC / SWIFT",
          text: "required for most Global accounts",
        },
      ],
      footer: (
        <>
          After saving, the account appears as a card. You can mark one account
          as <b>Default</b> — it will be used first when generating PEPPOL
          documents.
        </>
      ),
    },
    sections: [
      {
        title: "How bank details are selected automatically:",
        bullets: [
          <>
            The system matches the <b>invoice currency</b>
          </>,
          <>
            <b>Local</b> is used if buyer and seller are in the same country
          </>,
          <>
            <b>Global</b> is used if countries differ
          </>,
        ],
      },
    ],
    docLink: {
      text: "Need all supported currencies and required fields?",
      label: "Read the full bank details guide",
      href: `${developersBaseUrl}/linvoice/how-to-add-bank-details-in-lappa-invoice/`,
    },
    actions: [
      { label: "Go to Bank details", to: "/settings", variant: "primary" },
    ],
  },
};
